import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Link as RouterLink } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import ConfirmationDialog from 'components/confirmation-dialog'
import Box from '@material-ui/core/Box'
import ButtonBase from '@material-ui/core/ButtonBase'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import SectionTitle from 'components/section-title'
import { TipWellActionButton } from 'components/tip-well'
import SocialShareDialog from 'components/social-share-dialog'
import { isExternalBookingUrl } from 'lib/coach-profile/marketplace-listing'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import {
  interactiveCardFocusStyles,
  interactiveCardLinkResetStyles,
} from 'lib/styles/interactive-card-hover'
import { CREATE_GROUP_EVENT_NEW_PATH } from 'pages/pga-coach/bookings/events/create-event-form/quick-start-visibility'
import { BENCHMARK_LEADS_PATH } from 'pages/pga-coach/dashboard/performance-benchmarks/constants'
import { SettingsStatusBadge } from 'pages/pga-coach/settings/settings-components'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import { BUSINESS_BOTTLENECK_QUERY, CLEAR_EXTERNAL_BOOKING_URL } from './query'

const ROW_BORDER = '#E0E0E0'
const STEP_NUMBER_BACKGROUND = '#E0EAFF'
const STEP_NUMBER_COLOR = '#172024'
const STUDENTS_LIST_PATH = '/pga-coach/students/list'
const LAPSED_STUDENTS_LIST_PATH = `${STUDENTS_LIST_PATH}?${new URLSearchParams({
  status: 'LAPSED',
})}`
// The backend names this play by its title; it has no key that tells it apart
// from the other outreach plays.
const LAPSED_OUTREACH_PLAY_TITLE = 'Students who have not come back'
export const UPGRADE_SOURCE = 'business-bottleneck'
const UPGRADE_MUTATION = 'createPgaCoachSubscriptionPaymentSession'
const LEADS_AUTO_REPLY_PATH = '/pga-coach/settings/leads-auto-reply'
const ENABLE_LEAD_AUTO_REPLY_MUTATION = 'updateCoachPreferences'
const CLEAR_EXTERNAL_BOOKING_URL_MUTATION = 'updateCoachExternalBookingUrl'
const RUNNABLE_MUTATIONS = [
  CLEAR_EXTERNAL_BOOKING_URL_MUTATION,
  UPGRADE_MUTATION,
]

export const isUpgradePlay = play =>
  play.action === 'MUTATION' && play.mutationName === UPGRADE_MUTATION

const isClearBookingUrlPlay = play =>
  play.mutationName === CLEAR_EXTERNAL_BOOKING_URL_MUTATION

const isRunnablePlay = play =>
  play.action === 'MUTATION' && RUNNABLE_MUTATIONS.includes(play.mutationName)

const isPlayDone = (play, leadAutoReplyEnabled) =>
  play.mutationName === ENABLE_LEAD_AUTO_REPLY_MUTATION &&
  Boolean(leadAutoReplyEnabled)

const getPlayLocation = play => {
  if (play.mutationName === ENABLE_LEAD_AUTO_REPLY_MUTATION) {
    return LEADS_AUTO_REPLY_PATH
  }

  if (play.action !== 'OPEN_PATH' || !play.path) {
    return null
  }

  return play.path === CREATE_GROUP_EVENT_NEW_PATH
    ? { pathname: play.path, state: { skipQuickStart: true } }
    : play.path
}

const isLapsedOutreachPlay = play =>
  play.title?.trim().toLowerCase() === LAPSED_OUTREACH_PLAY_TITLE.toLowerCase()

const getPlayTarget = (play, shareUrl) => {
  if (play.outreachTargets?.length) {
    return {
      type: 'students',
      location: isLapsedOutreachPlay(play)
        ? LAPSED_STUDENTS_LIST_PATH
        : STUDENTS_LIST_PATH,
    }
  }

  if (play.action === 'SHARE_BOOKING_LINK') {
    return shareUrl ? { type: 'share' } : null
  }

  const location = getPlayLocation(play)

  if (location) {
    return { type: 'location', location }
  }

  return isRunnablePlay(play) ? { type: 'run' } : null
}

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${ROW_BORDER}`,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.75, 2),
    },
  },
  stepList: {
    display: 'flex',
    flexDirection: 'column',
  },
  step: {
    '& + &': {
      borderTop: `1px solid ${ROW_BORDER}`,
    },
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(2, 2.5),
    textAlign: 'left',
    cursor: 'pointer',
    ...interactiveCardLinkResetStyles,
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
    ...interactiveCardFocusStyles,
    '& *': {
      textDecoration: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.75, 2),
    },
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: STEP_NUMBER_BACKGROUND,
    color: STEP_NUMBER_COLOR,
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  stepCopy: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  stepTitle: {
    margin: 0,
    fontWeight: 600,
    fontSize: '0.9375rem',
    lineHeight: 1.3,
    color: '#172024',
  },
  stepTitleDone: {
    '&&': {
      textDecoration: 'line-through',
    },
    color: theme.palette.text.secondary,
  },
  stepDescription: {
    margin: theme.spacing(0.5, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
  },
  stepDescriptionDone: {
    color: theme.palette.text.disabled,
  },
  stepChevron: {
    flexShrink: 0,
    color: 'rgba(0, 0, 0, 0.38)',
    fontSize: 20,
    width: 20,
    height: 20,
  },
}))

export const useBottleneckPlayActions = ({ upgradeFlow, bookingUrl }) => {
  const [confirmingClear, setConfirmingClear] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const refetchQueries = [{ query: BUSINESS_BOTTLENECK_QUERY }]
  const [clearExternalUrl, { loading: clearing }] = useMutation(
    CLEAR_EXTERNAL_BOOKING_URL,
    { refetchQueries },
  )

  const reportResult = (result, successMessage, errorMessage) => {
    if (result?.success) {
      snackbarSuccess(result.message || successMessage)
    } else {
      snackbarError(result?.message || errorMessage)
    }
  }

  const clearBookingUrl = async () => {
    try {
      const { data } = await clearExternalUrl()
      reportResult(
        data?.updateCoachExternalBookingUrl,
        'Outside booking link removed',
        'Could not update booking URL',
      )
    } catch (error) {
      snackbarError(error.message)
    } finally {
      setConfirmingClear(false)
    }
  }

  const runPlay = async play => {
    try {
      if (play.mutationName === CLEAR_EXTERNAL_BOOKING_URL_MUTATION) {
        setConfirmingClear(true)
      } else if (play.mutationName === UPGRADE_MUTATION) {
        await upgradeFlow.handleUpgradeClick({ source: UPGRADE_SOURCE })
      }
    } catch (error) {
      snackbarError(error.message)
    }
  }

  return {
    runPlay,
    busy: clearing || Boolean(upgradeFlow.upgradeLoading),
    clearBookingUrlDialogProps: {
      open: confirmingClear,
      bookingUrl,
      disabled: clearing,
      onConfirm: clearBookingUrl,
      onCancel: () => setConfirmingClear(false),
    },
  }
}

export const ClearBookingUrlDialog = ({
  open,
  bookingUrl,
  disabled,
  onConfirm,
  onCancel,
}) => (
  <ConfirmationDialog
    open={open}
    heading="Remove your outside booking link?"
    message={
      isExternalBookingUrl(bookingUrl)
        ? `This removes ${bookingUrl} from your PGA Coach profile. You can add it back from your Booking URL settings.`
        : 'This removes your outside booking link from your PGA Coach profile. You can add it back from your Booking URL settings.'
    }
    confirmText="Remove link"
    disabled={disabled}
    onConfirm={onConfirm}
    onCancel={onCancel}
  />
)

ClearBookingUrlDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  bookingUrl: PropTypes.string,
  disabled: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

const StepCopy = ({ number, title, body, done, classes }) => {
  const settingsClasses = useSettingsStyles()

  return (
    <>
      <Box className={classes.stepNumber} aria-hidden>
        {number}
      </Box>
      <Box className={classes.stepCopy}>
        <Typography
          component="p"
          className={clsx(classes.stepTitle, done && classes.stepTitleDone)}
        >
          {title}
        </Typography>
        {body ? (
          <Typography
            component="p"
            className={clsx(
              classes.stepDescription,
              done && classes.stepDescriptionDone,
            )}
          >
            {body}
          </Typography>
        ) : null}
      </Box>
      {done ? (
        <SettingsStatusBadge classes={settingsClasses}>
          Done
        </SettingsStatusBadge>
      ) : (
        <ChevronRightIcon className={classes.stepChevron} aria-hidden />
      )}
    </>
  )
}

StepCopy.propTypes = {
  number: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string,
  done: PropTypes.bool,
  classes: PropTypes.object.isRequired,
}

const PlayStep = ({
  play,
  number,
  shareUrl,
  leadAutoReplyEnabled,
  onRun,
  busy,
  classes,
}) => {
  const copy = (
    <StepCopy
      number={number}
      title={play.title}
      body={play.body}
      done={isPlayDone(play, leadAutoReplyEnabled)}
      classes={classes}
    />
  )

  const target = getPlayTarget(play, shareUrl)

  if (!target) {
    return null
  }

  if (target.type === 'share') {
    return (
      <Box className={classes.step} data-testid="bottleneck-play">
        <SocialShareDialog
          trigger={<ButtonBase className={classes.stepRow}>{copy}</ButtonBase>}
          shareUrl={shareUrl}
          shareTitle="Booking Link"
          showBookingUrl
          att="bottleneck-booking-link"
        />
      </Box>
    )
  }

  if (target.type === 'run') {
    return (
      <Box className={classes.step} data-testid="bottleneck-play">
        <ButtonBase
          className={classes.stepRow}
          onClick={() => onRun(play)}
          disabled={busy}
        >
          {copy}
        </ButtonBase>
      </Box>
    )
  }

  return (
    <Box className={classes.step} data-testid="bottleneck-play">
      <RouterLink to={target.location} className={classes.stepRow}>
        {copy}
      </RouterLink>
    </Box>
  )
}

PlayStep.propTypes = {
  play: PropTypes.object.isRequired,
  number: PropTypes.number.isRequired,
  shareUrl: PropTypes.string,
  leadAutoReplyEnabled: PropTypes.bool,
  onRun: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
}

const isPlayVisible = (play, shareUrl) => Boolean(getPlayTarget(play, shareUrl))

export const getAvailablePlays = (plays, { hasPaidTier, bookingUrl }) =>
  (plays ?? []).filter(
    play =>
      !(hasPaidTier && isUpgradePlay(play)) &&
      !(isClearBookingUrlPlay(play) && !isExternalBookingUrl(bookingUrl)),
  )

export const getVisiblePlays = (plays, shareUrl) =>
  (plays ?? []).filter(play => isPlayVisible(play, shareUrl))

const getPrimaryPlay = (plays, shareUrl, leadAutoReplyEnabled) =>
  plays.find(
    play =>
      !isPlayDone(play, leadAutoReplyEnabled) &&
      Boolean(getPlayTarget(play, shareUrl)),
  )

export const BottleneckPrimaryAction = ({
  bottleneck,
  shareUrl,
  onRunPlay,
  busy,
  leadAutoReplyEnabled,
  className,
}) => {
  const play = getPrimaryPlay(
    bottleneck.plays ?? [],
    shareUrl,
    leadAutoReplyEnabled,
  )

  if (bottleneck.constraintKey === 'WASTED_DEMAND') {
    return (
      <TipWellActionButton
        className={className}
        component={RouterLink}
        to={BENCHMARK_LEADS_PATH}
      >
        View Leads
      </TipWellActionButton>
    )
  }

  if (!play) {
    return null
  }

  const target = getPlayTarget(play, shareUrl)

  if (target.type === 'share') {
    return (
      <SocialShareDialog
        trigger={
          <TipWellActionButton className={className}>
            Share link
          </TipWellActionButton>
        }
        shareUrl={shareUrl}
        shareTitle="Booking Link"
        showBookingUrl
        att="bottleneck-booking-link"
      />
    )
  }

  if (target.type === 'run') {
    return (
      <TipWellActionButton
        className={className}
        onClick={() => onRunPlay(play)}
        disabled={busy}
      >
        {play.mutationName === UPGRADE_MUTATION
          ? 'Upgrade to Coach+'
          : 'Do this now'}
      </TipWellActionButton>
    )
  }

  return (
    <TipWellActionButton
      className={className}
      component={RouterLink}
      to={target.location}
    >
      Get started
    </TipWellActionButton>
  )
}

BottleneckPrimaryAction.propTypes = {
  className: PropTypes.string,
  bottleneck: PropTypes.shape({
    constraintKey: PropTypes.string,
    plays: PropTypes.array,
  }).isRequired,
  shareUrl: PropTypes.string,
  leadAutoReplyEnabled: PropTypes.bool,
  onRunPlay: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
}

const BottleneckInsight = ({
  bottleneck,
  shareUrl,
  onRunPlay,
  busy,
  leadAutoReplyEnabled,
}) => {
  const classes = useStyles()
  const plays = getVisiblePlays(bottleneck.plays, shareUrl)

  return (
    <>
      {plays.length ? (
        <Box>
          <SectionTitle component="h3" className={classes.sectionTitle}>
            Recommended next steps
          </SectionTitle>
          <Box className={classes.stepList}>
            {plays.map((play, index) => (
              <PlayStep
                key={`${index}-${play.title}`}
                play={play}
                number={index + 1}
                shareUrl={shareUrl}
                leadAutoReplyEnabled={leadAutoReplyEnabled}
                onRun={onRunPlay}
                busy={busy}
                classes={classes}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </>
  )
}

BottleneckInsight.propTypes = {
  bottleneck: PropTypes.shape({
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    plays: PropTypes.array,
  }).isRequired,
  shareUrl: PropTypes.string,
  leadAutoReplyEnabled: PropTypes.bool,
  onRunPlay: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
}

export default BottleneckInsight
